import { getAccessToken } from "../awsAuth";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const ACTIVITY_URL = `${API_BASE}/api/activity`;

const DEFAULT_DEDUPE_MS = 8000;

function makeDedupeKey(payload = {}) {
  return [
    "cvanto:activity",
    payload.eventType || "",
    payload.targetType || "",
    payload.targetId || "",
    payload.category || "",
    payload.query || "",
    payload.targetUserId || "",
    payload.collectionType || "",
    payload.collectionTitle || "",
  ].join(":");
}

function shouldSkipByDedupe(payload, dedupeMs) {
  if (typeof sessionStorage === "undefined") return false;

  try {
    const key = makeDedupeKey(payload);
    const last = Number(sessionStorage.getItem(key) || "0") || 0;
    const now = Date.now();

    if (last && now - last < dedupeMs) return true;

    sessionStorage.setItem(key, String(now));
    return false;
  } catch {
    return false;
  }
}

export async function trackActivity(payload = {}, options = {}) {
  try {
    const eventType = String(payload?.eventType || "").trim();

    if (!eventType) return false;

    const dedupeMs =
      typeof options.dedupeMs === "number"
        ? options.dedupeMs
        : DEFAULT_DEDUPE_MS;

    if (dedupeMs > 0 && shouldSkipByDedupe(payload, dedupeMs)) {
      return false;
    }

    const token = await getAccessToken();

    if (!token) return false;

    const res = await fetch(ACTIVITY_URL, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        eventType,

        targetType: payload.targetType || null,
        targetId: payload.targetId || null,

        category: payload.category || null,
        query: payload.query || null,

        targetUserId: payload.targetUserId || null,

        collectionType: payload.collectionType || null,
        collectionTitle: payload.collectionTitle || null,

        durationMs: payload.durationMs || 0,
      }),
    });

    return res.ok;
  } catch {
    return false;
  }
}

export function trackOpenCategory(category) {
  return trackActivity(
    {
      eventType: "OPEN_CATEGORY",
      targetType: "category",
      targetId: category,
      category,
    },
    { dedupeMs: 15000 }
  );
}

export function trackSearchQuery(payload = {}) {
  const query =
    typeof payload === "string"
      ? payload
      : String(payload?.query || "").trim();

  if (!query || query.length < 2) return;

  return trackActivity({
    eventType: "SEARCH_QUERY",
    targetType: "search",
    targetId: query.toLowerCase(),
    query,
    score: 4,
    meta: {
      tab: payload?.tab || null,
      category: payload?.category || null,
      city: payload?.city || null,
    },
  });
}

export function trackOpenListingDetails({ listingId, category } = {}) {
  if (!listingId) return false;

  return trackActivity(
    {
      eventType: "OPEN_LISTING_DETAILS",
      targetType: "listing",
      targetId: listingId,
      category,
    },
    { dedupeMs: 10000 }
  );
}

export function trackDwellListing({ listingId, category, durationMs } = {}) {
  if (!listingId) return false;

  return trackActivity(
    {
      eventType: "DWELL_LISTING",
      targetType: "listing",
      targetId: listingId,
      category,
      durationMs,
    },
    { dedupeMs: 30000 }
  );
}

export function trackSaveListing({ listingId, category } = {}) {
  if (!listingId) return false;

  return trackActivity(
    {
      eventType: "SAVE_LISTING",
      targetType: "listing",
      targetId: listingId,
      category,
    },
    { dedupeMs: 5000 }
  );
}

export function trackLikeListing({ listingId, category } = {}) {
  if (!listingId) return false;

  return trackActivity(
    {
      eventType: "LIKE_LISTING",
      targetType: "listing",
      targetId: listingId,
      category,
    },
    { dedupeMs: 5000 }
  );
}

export function trackMessageUser({ targetUserId } = {}) {
  if (!targetUserId) return false;

  return trackActivity(
    {
      eventType: "MESSAGE_USER",
      targetType: "profile",
      targetId: targetUserId,
      targetUserId,
    },
    { dedupeMs: 15000 }
  );
}

export function trackOpenProfile({ targetUserId } = {}) {
  if (!targetUserId) return false;

  return trackActivity(
    {
      eventType: "OPEN_PROFILE",
      targetType: "profile",
      targetId: targetUserId,
      targetUserId,
    },
    { dedupeMs: 15000 }
  );
}

export function trackOpenCollection(collection = {}) {
  const collectionId = collection?.id || collection?.type || collection?.title;

  if (!collectionId) return false;

  return trackActivity(
    {
      eventType: "OPEN_COLLECTION",
      targetType: "collection",
      targetId: collectionId,
      collectionType: collection?.type || null,
      collectionTitle: collection?.title || null,
    },
    { dedupeMs: 10000 }
  );
}